/**
 * Cross-tab logout signaling. When one tab logs out, every other tab sharing
 * the same auth scope is notified so it can clear its session and close.
 *
 * Tabs opened by the user (not via window.open) cannot be force-closed by
 * script — browsers block it. window.close() is attempted anyway; it
 * succeeds for script-opened tabs and silently no-ops for the rest, which is
 * why callers must also clear session state / redirect to login regardless.
 */

const MARKER_PREFIX = '__cross_tab_logout__';

const channels = {};

function getChannel(scope) {
  if (typeof BroadcastChannel === 'undefined') return null;
  if (!channels[scope]) {
    channels[scope] = new BroadcastChannel(`cross-tab-logout:${scope}`);
  }
  return channels[scope];
}

export function broadcastLogout(scope, returnUrl = null) {
  const channel = getChannel(scope);
  const message = { type: 'logout', ts: Date.now(), returnUrl };
  if (channel) {
    try {
      channel.postMessage(message);
    } catch (e) {
      // ignore
    }
  }
  try {
    localStorage.setItem(`${MARKER_PREFIX}${scope}`, JSON.stringify(message));
  } catch (e) {
    // ignore
  }
}

export function onLogoutBroadcast(scope, handler) {
  const channel = getChannel(scope);
  const onMessage = (event) => {
    if (event?.data?.type === 'logout') handler(event.data.returnUrl || null);
  };
  if (channel) channel.addEventListener('message', onMessage);

  const markerKey = `${MARKER_PREFIX}${scope}`;
  const onStorage = (event) => {
    if (event.key === markerKey && event.newValue) {
      let returnUrl = null;
      try {
        returnUrl = JSON.parse(event.newValue)?.returnUrl || null;
      } catch (e) {
        // ignore
      }
      handler(returnUrl);
    }
  };
  window.addEventListener('storage', onStorage);

  return () => {
    if (channel) channel.removeEventListener('message', onMessage);
    window.removeEventListener('storage', onStorage);
  };
}

export function attemptCloseTab() {
  try {
    window.close();
  } catch (e) {
    // ignore — blocked for tabs not opened via script
  }
}
